import { useState, FormEvent } from "react";
import AnimatedSection from "../../../components/base/AnimatedSection";

export default function ContactSection() {
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    tipo_consulta: "",
    descripcion: "",
    rgpd: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const target = e.target;
    const value = target.type === "checkbox" ? (target as HTMLInputElement).checked : target.value;
    setFormData((prev) => ({ ...prev, [target.name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    if (!formData.rgpd) {
      setError("Debes aceptar la política de privacidad para continuar.");
      return;
    }
    if (formData.descripcion.length > 500) {
      setError("La descripción no puede superar los 500 caracteres.");
      return;
    }
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        nombre: formData.nombre,
        email: formData.email,
        telefono: formData.telefono,
        tipo_consulta: formData.tipo_consulta,
        descripcion: formData.descripcion,
        rgpd: formData.rgpd ? "Acepto la política de privacidad" : "",
      });
      const res = await fetch("https://readdy.ai/api/form/d71vqgk991ove783t7v0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError("Hubo un problema al enviar el formulario. Por favor inténtalo de nuevo.");
      }
    } catch {
      setError("Error de conexión. Por favor inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-8 h-0.5" style={{ backgroundColor: "#E8150C" }}></div>
            <span className="font-body text-brand-red text-sm font-medium tracking-widest uppercase">
              Hablemos
            </span>
          </div>
          <h2 className="font-heading font-black text-brand-black leading-none mb-14"
            style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
            CONTACTA CON FAIR PLAY GLOBAL
          </h2>
        </AnimatedSection>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Left info */}
          <AnimatedSection className="lg:col-span-2" delay={0}>
            <div className="space-y-8">
              <div>
                <p className="font-body text-brand-gray-mid text-sm leading-relaxed mb-8">
                  ¿Quieres profesionalizar la gestión de tu liga? Cuéntanos tu caso y te respondemos en menos de <strong className="text-brand-anthracite">24h laborables</strong>.
                </p>
              </div>

              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md border border-brand-pearl">
                    <i className="ri-mail-line text-brand-red text-lg"></i>
                  </div>
                  <div>
                    <p className="font-body text-xs text-brand-gray-mid uppercase tracking-wide">Email</p>
                    <p className="font-body text-brand-black text-sm font-medium">info@fairplayglobal.es</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md border border-brand-pearl">
                    <i className="ri-phone-line text-brand-red text-lg"></i>
                  </div>
                  <div>
                    <p className="font-body text-xs text-brand-gray-mid uppercase tracking-wide">Teléfono</p>
                    <p className="font-body text-brand-black text-sm font-medium">+34 658 30 61 63</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md border border-brand-pearl">
                    <i className="ri-time-line text-brand-red text-lg"></i>
                  </div>
                  <div>
                    <p className="font-body text-xs text-brand-gray-mid uppercase tracking-wide">Horario</p>
                    <p className="font-body text-brand-black text-sm font-medium">Lun–Vie: 9:00–18:00</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md border border-brand-pearl">
                    <i className="ri-instagram-line text-brand-red text-lg"></i>
                  </div>
                  <div>
                    <p className="font-body text-xs text-brand-gray-mid uppercase tracking-wide">Instagram</p>
                    <p className="font-body text-brand-black text-sm font-medium">@fairplayglobal</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md border border-brand-pearl">
                    <i className="ri-linkedin-line text-brand-red text-lg"></i>
                  </div>
                  <div>
                    <p className="font-body text-xs text-brand-gray-mid uppercase tracking-wide">LinkedIn</p>
                    <p className="font-body text-brand-black text-sm font-medium">Fair Play Global</p>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-brand-pearl">
                <p className="font-body text-brand-gray-mid text-xs leading-relaxed">
                  Respondemos en menos de 24h laborables. Todos los datos tratados conforme al RGPD.
                </p>
              </div>
            </div>
          </AnimatedSection>

          {/* Right form */}
          <AnimatedSection className="lg:col-span-3" delay={100}>
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-20 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full mb-5" style={{ backgroundColor: "#E8150C" }}>
                  <i className="ri-check-line text-white text-2xl"></i>
                </div>
                <h3 className="font-heading font-bold text-brand-black text-2xl mb-3">
                  ¡Mensaje recibido!
                </h3>
                <p className="font-body text-brand-gray-mid text-sm">
                  Te contactaremos en menos de 24 horas laborables.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-body text-xs text-brand-anthracite font-medium uppercase tracking-wide mb-2">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      required
                      value={formData.nombre}
                      onChange={handleChange}
                      placeholder="Tu nombre completo"
                      className="w-full border border-brand-pearl rounded-md px-4 py-3 font-body text-sm text-brand-black placeholder-brand-gray-mid focus:outline-none focus:border-brand-red transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs text-brand-anthracite font-medium uppercase tracking-wide mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@email.com"
                      className="w-full border border-brand-pearl rounded-md px-4 py-3 font-body text-sm text-brand-black placeholder-brand-gray-mid focus:outline-none focus:border-brand-red transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-body text-xs text-brand-anthracite font-medium uppercase tracking-wide mb-2">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      placeholder="+34 658 30 61 63"
                      className="w-full border border-brand-pearl rounded-md px-4 py-3 font-body text-sm text-brand-black placeholder-brand-gray-mid focus:outline-none focus:border-brand-red transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs text-brand-anthracite font-medium uppercase tracking-wide mb-2">
                      Tipo de consulta *
                    </label>
                    <select
                      name="tipo_consulta"
                      required
                      value={formData.tipo_consulta}
                      onChange={handleChange}
                      className="w-full border border-brand-pearl rounded-md px-4 py-3 font-body text-sm text-brand-black focus:outline-none focus:border-brand-red transition-colors cursor-pointer bg-white"
                    >
                      <option value="">Selecciona una opción</option>
                      <option value="Información servicios">Información servicios</option>
                      <option value="Ser colaborador">Ser colaborador</option>
                      <option value="Consulta general">Consulta general</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block font-body text-xs text-brand-anthracite font-medium uppercase tracking-wide mb-2">
                    Descripción de tu liga
                  </label>
                  <textarea
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    placeholder="Cuéntanos sobre tu liga, número de equipos, categorías..."
                    rows={5}
                    maxLength={500}
                    className="w-full border border-brand-pearl rounded-md px-4 py-3 font-body text-sm text-brand-black placeholder-brand-gray-mid focus:outline-none focus:border-brand-red transition-colors resize-none"
                  />
                  <p className="font-body text-xs text-brand-gray-mid text-right mt-1">
                    {formData.descripcion.length}/500
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="rgpd"
                    id="rgpd"
                    checked={formData.rgpd}
                    onChange={handleChange}
                    className="mt-1 w-4 h-4 accent-brand-red cursor-pointer flex-shrink-0"
                  />
                  <label htmlFor="rgpd" className="font-body text-brand-gray-mid text-xs leading-relaxed cursor-pointer">
                    He leído y acepto la{" "}
                    <a href="/privacidad" className="text-brand-red hover:underline">
                      política de privacidad
                    </a>{" "}
                    y consiento el tratamiento de mis datos para gestionar mi consulta.
                  </label>
                </div>

                {error && (
                  <p className="font-body text-brand-red text-sm">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-brand-red hover:bg-brand-red-hover text-white font-body font-medium py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-60"
                >
                  {submitting ? "Enviando..." : "ENVIAR MENSAJE"}
                </button>
              </form>
            )}
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}